import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useSearchParams } from "react-router-dom";
import { SolutionTask } from "../types";
import { 
  getProjectTasks, 
  downloadInputFile, 
  submitDesktopSolution, 
  submitWebSolution,
  getProblemRuns,
  createScoreDispute,
  deleteScoreDispute,
  clearDisputeCache,
  deleteSolution,
  getPlaygroundPreviewStatus,
  PlaygroundPreview
} from "../api";
import PlaygroundRecordingModal from "./PlaygroundRecordingModal";
import "./SolutionTaskViewer.css";

interface SolutionTaskViewerProps {
  projectId: string;
  onBack: () => void;
}

const SolutionTaskViewer: React.FC<SolutionTaskViewerProps> = ({
  projectId,
  onBack,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [tasks, setTasks] = useState<SolutionTask[]>([]);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(() => {
    const taskParam = new URLSearchParams(window.location.search).get('task');
    return taskParam ? Math.max(0, parseInt(taskParam) - 1) : 0;
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [solutionFiles, setSolutionFiles] = useState<Record<string, File>>({});
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submittedTasks, setSubmittedTasks] = useState<Set<string>>(new Set());
  const [disputedTasks, setDisputedTasks] = useState<Map<string, { disputeId: string; reason?: string }>>(new Map()); // problemId -> {disputeId, reason}
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [disputeError, setDisputeError] = useState<string | null>(null);
  const [deletingLoading, setDeletingLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [solutionToDelete, setSolutionToDelete] = useState<string | null>(null);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [playgroundPreview, setPlaygroundPreview] = useState<PlaygroundPreview | null>(null);
  const [creatingPlayground, setCreatingPlayground] = useState(false);
  const [showImpossibleTaskModal, setShowImpossibleTaskModal] = useState(false);
  const [impossibleTaskReason, setImpossibleTaskReason] = useState("");

  useEffect(() => {
    loadTasks();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Only sync state with URL changes (for browser back/forward navigation and initial load)
  useEffect(() => {
    const taskParam = searchParams.get('task');
    const taskFromUrl = taskParam ? Math.max(0, parseInt(taskParam) - 1) : 0;
    
    // Only update state if URL differs and we have loaded tasks
    if (taskFromUrl !== currentTaskIndex && tasks.length > 0 && taskFromUrl < tasks.length) {
      setCurrentTaskIndex(taskFromUrl);
    }
  }, [searchParams, tasks.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear submission states when task changes (but keep solution files and submitted tasks)
  useEffect(() => {
    setFileError(null);
    setDisputeError(null);
  }, [currentTaskIndex]);

  // Initialize disputed tasks from the loaded task data
  useEffect(() => {
    const disputedMap = new Map<string, { disputeId: string; reason?: string }>();
    
    // Populate disputedTasks from the task data
    for (const task of tasks) {
      if (task.dispute) {
        disputedMap.set(task.problem.id, {
          disputeId: task.dispute.dispute_id,
          reason: task.dispute.reason
        });
      }
    }
    
    setDisputedTasks(disputedMap);
  }, [tasks]); // Only run when tasks change

  // Get current task's solution file
  const currentTask = tasks[currentTaskIndex];
  const currentTaskId = currentTask?.problem.id;
  const currentSolutionFile = currentTaskId ? solutionFiles[currentTaskId] : null;

  const loadTasks = async (shouldAutoSelectTask: boolean = true) => {
    try {
      setLoading(true);
      const projectTasks = await getProjectTasks(projectId);
      setTasks(projectTasks);
      
      // Set default URL parameter if none exists
      const currentTaskParam = searchParams.get('task');
      if (!currentTaskParam) {
        // Only auto-select first incomplete task on initial load, not after submissions
        if (shouldAutoSelectTask) {
          const firstIncompleteIndex = projectTasks.findIndex(task => !task.is_completed);
          const defaultTaskIndex = firstIncompleteIndex >= 0 ? firstIncompleteIndex : 0;
          setSearchParams(prev => {
            const newParams = new URLSearchParams(prev.toString());
            newParams.set('task', (defaultTaskIndex + 1).toString());
            return newParams;
          }, { replace: true });
        }
      }
      
      setError(null);
    } catch (err: any) {
      console.error("Error loading tasks:", err);
      setError("Failed to load tasks. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPlayground = async () => {
    const currentTask = tasks[currentTaskIndex];
    if (!currentTask || !currentTaskId) return;

    try {
      setCreatingPlayground(true);
      setFileError(null);
      
      const software = currentTask.problem.software.toLowerCase();
      
      // Start preloading next task's playground if it exists and is a web app
      if (currentTaskIndex < tasks.length - 1) {
        const nextTask = tasks[currentTaskIndex + 1];
        if (nextTask && isWebApp(nextTask.problem.software)) {
          // Fire and forget - preload next playground
          getPlaygroundPreviewStatus(
            nextTask.problem.software.toLowerCase(),
            nextTask.problem.id
          ).catch(err => {
            console.log('[Preload] Failed to preload next playground:', err.message);
          });
          console.log('[Preload] Started preloading playground for next task:', nextTask.problem.id);
        }
      }
      
      // Load current task's playground
      let preview = await getPlaygroundPreviewStatus(software, currentTaskId);
      
      // Poll if status is "creating"
      const maxAttempts = 60; // 5 minutes max (60 * 5s)
      let attempts = 0;
      
      while (preview.status === 'creating' && attempts < maxAttempts) {
        console.log('[Playground] Status: creating, polling...', { attempt: attempts + 1, maxAttempts });
        await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds
        
        try {
          preview = await getPlaygroundPreviewStatus(software, currentTaskId);
        } catch (pollError) {
          console.error('[Playground] Polling error:', pollError);
          // Continue polling on transient errors
        }
        
        attempts++;
      }
      
      if (preview.status === 'failed') {
        throw new Error('Playground creation failed');
      }
      
      if (preview.status === 'creating' && attempts >= maxAttempts) {
        throw new Error('Playground creation timed out');
      }
      
      // Playground should now be ready
      setPlaygroundPreview(preview);
      setShowRecordingModal(true);
    } catch (err: any) {
      console.error("Error creating playground:", err);
      alert(`Failed to create playground: ${err.message || 'Please try again.'}`);
    } finally {
      setCreatingPlayground(false);
    }
  };

  const handlePlaygroundSolutionSubmitted = () => {
    // Reload tasks to update completion status
    loadTasks(false);
    setShowRecordingModal(false);
    setPlaygroundPreview(null);
  };

  const handleSubmitImpossibleTask = async () => {
    const currentTask = tasks[currentTaskIndex];
    if (!currentTask || !currentTaskId) return;

    if (!impossibleTaskReason.trim()) {
      setFileError("Please provide an explanation for why this task is impossible");
      return;
    }

    try {
      setUploading(true);
      setFileError(null);
      
      const software = currentTask.problem.software.toLowerCase();
      const isWebAppSoftware = isWebApp(software);
      
      if (isWebAppSoftware) {
        // Web app submission for impossible task
        await submitWebSolution({
          problem_id: currentTask.problem.id,
          solution_project_id: projectId,
          playground_session_id: "impossible-task",
          submission_notes: `IMPOSSIBLE TASK: ${impossibleTaskReason.trim()}`,
          final_state: { impossible: true, reason: impossibleTaskReason.trim() }
        });
      } else {
        // Desktop app submission for impossible task - submit with empty solution_data
        await submitDesktopSolution({
          problem_id: currentTask.problem.id,
          solution_project_id: projectId,
          solution_files: [], // No files for impossible task
          submission_notes: `IMPOSSIBLE TASK: ${impossibleTaskReason.trim()}`
        });
      }
      
      // Close the modal
      setShowImpossibleTaskModal(false);
      setImpossibleTaskReason("");
      
      // Reload tasks to update completion status (but don't auto-switch tasks)
      await loadTasks(false);
      
    } catch (err: any) {
      console.error("Error submitting impossible task:", err);
      alert("Failed to submit impossible task explanation. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmitSolution = async () => {
    const currentTask = tasks[currentTaskIndex];
    if (!currentTask || !currentTaskId || !currentSolutionFile) return;

    try {
      setUploading(true);
      setFileError(null);
      
      const software = currentTask.problem.software.toLowerCase();
      const isWebAppSoftware = isWebApp(software);
      
      if (isWebAppSoftware) {
        // Web app submission - for now use placeholder until playground is implemented
        await submitWebSolution({
          problem_id: currentTask.problem.id,
          solution_project_id: projectId,
          playground_session_id: "placeholder-session-id",
          submission_notes: "Web solution submitted via platform",
          final_state: { placeholder: "state" }
        });
      } else {
        // Desktop app submission with actual file
        await submitDesktopSolution({
          problem_id: currentTask.problem.id,
          solution_project_id: projectId,
          solution_files: [currentSolutionFile],
          submission_notes: "Desktop solution submitted via platform"
        });
      }

      // Don't mark task as submitted since we want to allow multiple submissions
      // The task completion status will be updated when we reload tasks
      
      // Clear the current file so the additional upload area is ready
      if (currentTaskId) {
        setSolutionFiles(prev => {
          const newFiles = { ...prev };
          delete newFiles[currentTaskId];
          return newFiles;
        });
      }
      
      // Reload tasks to update completion status (but don't auto-switch tasks)
      await loadTasks(false);
      
    } catch (err: any) {
      console.error("Error uploading solution:", err);
      
      // Handle NotImplementedError specially  
      if (err.response?.status === 501) {
        alert("Submission functionality is not yet fully implemented. This is expected for the current demo.");
      } else {
        alert("Failed to upload solution. Please try again.");
      }
    } finally {
      setUploading(false);
    }
  };


  const handlePrevious = () => {
    if (currentTaskIndex > 0) {
      const newTaskIndex = currentTaskIndex - 1;
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev.toString());
        newParams.set('task', (newTaskIndex + 1).toString());
        return newParams;
      });
    }
  };

  const handleNext = () => {
    if (currentTaskIndex < tasks.length - 1) {
      const newTaskIndex = currentTaskIndex + 1;
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev.toString());
        newParams.set('task', (newTaskIndex + 1).toString());
        return newParams;
      });
    }
  };

  const canNavigateNext = () => {
    return currentTaskIndex < tasks.length - 1; // Can navigate if not on last task
  };

  const isLastTask = () => {
    return currentTaskIndex === tasks.length - 1;
  };

  const areAllSolutionsSubmitted = () => {
    return tasks.every(task => 
      task.is_completed || 
      (task.problem.id && submittedTasks.has(task.problem.id)) ||
      (task.problem.id && disputedTasks.has(task.problem.id))
    );
  };

  const handleFinishProject = () => {
    // Navigate back to the home page (projects list)
    onBack();
  };

  const handleRemoveDispute = async () => {
    const currentTask = tasks[currentTaskIndex];
    if (!currentTask || !currentTaskId) return;
    
    const disputeInfo = disputedTasks.get(currentTaskId);
    if (!disputeInfo) return;

    try {
      setDisputeLoading(true);
      setDisputeError(null);
      
      // Delete the dispute
      await deleteScoreDispute(disputeInfo.disputeId);
      
      // Clear cache for this problem so future checks get fresh data
      clearDisputeCache(currentTaskId);
      
      // Remove from disputed tasks map
      setDisputedTasks(prev => {
        const newMap = new Map(prev);
        newMap.delete(currentTaskId);
        return newMap;
      });
      
      setDisputeError(null);
    } catch (err: any) {
      console.error("Error removing dispute:", err);
      setDisputeError(err.response?.data?.detail || "Failed to remove dispute. Please try again.");
    } finally {
      setDisputeLoading(false);
    }
  };

  const handleMarkAsBadTask = () => {
    const currentTask = tasks[currentTaskIndex];
    if (!currentTask || !currentTaskId) return;
    
    if (disputedTasks.has(currentTaskId)) {
      setDisputeError("This task has already been marked as 'Bad Task'");
      return;
    }
    
    // Open the modal to get the reason
    setShowDisputeModal(true);
    setDisputeReason("");
    setDisputeError(null);
  };

  const handleSubmitDispute = async () => {
    const currentTask = tasks[currentTaskIndex];
    if (!currentTask || !currentTaskId) return;
    
    if (!disputeReason.trim()) {
      setDisputeError("Please provide a reason for marking this task as bad");
      return;
    }

    try {
      setDisputeLoading(true);
      setDisputeError(null);
      
      // Get problem runs for this problem
      const runsResponse = await getProblemRuns(currentTaskId);
      if (!runsResponse.items || runsResponse.items.length === 0) {
        setDisputeError("No problem runs found for this task. Cannot create dispute.");
        return;
      }
      
      // Create dispute on the most recent problem run with the reason
      const mostRecentRun = runsResponse.items[runsResponse.items.length - 1];
      const dispute = await createScoreDispute(mostRecentRun.id, disputeReason.trim());
      
      // Clear cache for this problem so future checks get fresh data
      clearDisputeCache(currentTaskId);
      
      // Add to disputed tasks map with dispute ID and reason
      setDisputedTasks(prev => {
        const newMap = new Map(prev);
        newMap.set(currentTaskId, { 
          disputeId: dispute.id,
          reason: disputeReason.trim()
        });
        return newMap;
      });
      
      // Clear any file that was selected for this task
      if (currentTaskId && solutionFiles[currentTaskId]) {
        setSolutionFiles(prev => {
          const newFiles = { ...prev };
          delete newFiles[currentTaskId];
          return newFiles;
        });
      }
      
      // Close the modal
      setShowDisputeModal(false);
      setDisputeReason("");
      setDisputeError(null);
    } catch (err: any) {
      console.error("Error marking task as bad:", err);
      setDisputeError(err.response?.data?.detail || "Failed to mark task as bad. Please try again.");
    } finally {
      setDisputeLoading(false);
    }
  };


  const isWebApp = (software: string): boolean => {
    // These software types should open in embedded sandbox
    return ["immich", "odoo", "erpnext"].includes(software.toLowerCase());
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    const currentTask = tasks[currentTaskIndex];
    if (!currentTask) return;

    const software = currentTask.problem.software.toLowerCase();
    
    // Validate file type based on software (case-insensitive)
    const validExtensions = getValidExtensions(software);
    const fileExtension = file.name.split('.').pop()?.toLowerCase(); // Convert to lowercase for comparison
    const validExtensionsLower = validExtensions.map(ext => ext.toLowerCase());
    
    if (validExtensionsLower.length > 0 && !validExtensionsLower.includes(fileExtension || '')) {
      setFileError(`Invalid file type. Expected: ${validExtensions.join(', ')} (case-insensitive)`);
      return;
    }

    // Check file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      setFileError('File too large. Maximum size is 100MB.');
      return;
    }

    // Store file for current task
    if (currentTaskId) {
      setSolutionFiles(prev => ({
        ...prev,
        [currentTaskId]: file
      }));
    }
    setFileError(null);
  };

  const getValidExtensions = (software: string): string[] => {
    const extensionMap: Record<string, string[]> = {
      'freecad': ['FCStd'],
      'kicad': ['zip'],
      'blender': ['blend'],
      'musescore': ['mscz']
    };
    return extensionMap[software] || [];
  };

  // Generate case variations for file input accept attribute
  const getAcceptExtensions = (extensions: string[]): string => {
    const variants: string[] = [];
    extensions.forEach(ext => {
      variants.push(`.${ext}`); // Original case
      variants.push(`.${ext.toLowerCase()}`); // Lowercase
      variants.push(`.${ext.toUpperCase()}`); // Uppercase
    });
    // Remove duplicates and join
    return Array.from(new Set(variants)).join(',');
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDownloadFile = async () => {
    const currentTask = tasks[currentTaskIndex];
    if (!currentTask) return;

    try {
      await downloadInputFile(currentTask.problem.id);
    } catch (err: any) {
      console.error("Error downloading input file:", err);
    }
  };

  const handleDeleteSolution = async () => {
    if (!solutionToDelete) return;

    try {
      setDeletingLoading(true);
      setFileError(null);
      
      // Delete the specific solution
      await deleteSolution(solutionToDelete);
      
      // Check if this was the last solution for the task
      const currentTask = tasks[currentTaskIndex];
      if (currentTask && currentTask.solutions && currentTask.solutions.length === 1) {
        // Remove from submitted tasks if this was the last solution
        setSubmittedTasks(prev => {
          const newSet = new Set(prev);
          newSet.delete(currentTaskId || '');
          return newSet;
        });
      }
      
      // Clear the file for this task if it exists
      if (currentTaskId && solutionFiles[currentTaskId]) {
        setSolutionFiles(prev => {
          const newFiles = { ...prev };
          delete newFiles[currentTaskId];
          return newFiles;
        });
      }
      
      // Reload tasks to update the UI
      await loadTasks(false);
      
      // Close confirmation dialog and reset
      setShowDeleteConfirm(false);
      setSolutionToDelete(null);
      
    } catch (err: any) {
      console.error("Error deleting solution:", err);
      setFileError("Failed to delete solution. Please try again.");
    } finally {
      setDeletingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="task-viewer">
        <div className="task-header">
          <button onClick={onBack} className="back-button">
            ← Back to Projects
          </button>
        </div>
        <div className="loading">Loading tasks...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="task-viewer">
        <div className="task-header">
          <button onClick={onBack} className="back-button">
            ← Back to Projects
          </button>
        </div>
        <div className="error-message">{error}</div>
        <button onClick={() => loadTasks()} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="task-viewer">
        <div className="task-header">
          <button onClick={onBack} className="back-button">
            ← Back to Projects
          </button>
        </div>
        <div className="no-tasks">No tasks available in this project.</div>
      </div>
    );
  }


  return (
    <div className="task-viewer">
      <div className="task-header">
        <button onClick={onBack} className="back-button">
          ← Back to Projects
        </button>
      </div>

      <div className="task-scrollable-content">
        {/* Progress Indicator at Top */}
        <div className="progress-section">
          <div className="task-progress-indicator">
            {tasks.map((task, index) => {
              // Add a line break every 25 items
              const isNewRow = index > 0 && index % 25 === 0;
              return (
                <React.Fragment key={task.problem.id}>
                  {isNewRow && <div style={{ flexBasis: '100%', height: 0 }} />}
                  <div
                    className={`progress-dot ${index === currentTaskIndex ? 'active' : ''} ${
                      task.problem.spec.possible === false ? (task.is_completed ? 'impossible-completed' : 'impossible') :
                      task.is_completed || disputedTasks.has(task.problem.id) ? 'completed' : 
                      solutionFiles[task.problem.id] ? 'has-file' : 'empty'
                    }`}
                    onClick={() => {
                      setSearchParams(prev => {
                        const newParams = new URLSearchParams(prev.toString());
                        newParams.set('task', (index + 1).toString());
                        return newParams;
                      });
                    }}
                    title={`Task ${index + 1}: ${task.problem.name}${task.problem.spec.possible === false ? ' (Impossible Task)' : disputedTasks.has(task.problem.id) ? ' (Bad Task)' : ''}`}
                  />
                </React.Fragment>
              );
            })}
          </div>
          <div className="progress-label">
            Task {currentTaskIndex + 1} of {tasks.length}
          </div>
        </div>

        <div className="task-content">
        {!currentTask ? (
          <div className="error-message">
            No task selected or task data is unavailable. Please try refreshing the page.
          </div>
        ) : (
          <>
        <div className="task-info">
          <h2>{currentTask.problem.name}</h2>
          <div className="task-status">
            {currentTask.is_completed || (currentTaskId && disputedTasks.has(currentTaskId)) ? (
              <span className="status-completed">
                {currentTaskId && disputedTasks.has(currentTaskId) ? '⚠️ Bad Task' : '✓ Completed'}
              </span>
            ) : (
              <span className="status-pending">○ Pending</span>
            )}
          </div>
        </div>

        <div className="task-description">
          <h3>Task Description</h3>
          <p>{currentTask.problem.spec.prompt}</p>
        </div>

        <div className="task-files">
          <h3>Input Files</h3>
          {currentTask.problem.spec.input_file ? (
            <div className="file-item">
              <span>📁 {currentTask.problem.spec.input_file}</span>
              {isWebApp(currentTask.problem.software) ? (
                <button 
                  className="sandbox-button"
                  onClick={() => alert("Embedded sandbox integration not yet implemented")}
                >
                  Open in Sandbox
                </button>
              ) : (
                <button 
                  className="download-button"
                  onClick={handleDownloadFile}
                >
                  Download
                </button>
              )}
            </div>
          ) : (
            <p>No input files required</p>
          )}
        </div>

        <div className="solution-section">
          <h3>Submit Solution</h3>
          
          {/* Show if task is disputed */}
          {currentTaskId && disputedTasks.has(currentTaskId) && (
            <div className="dispute-notice">
              <span className="dispute-icon">⚠️</span>
              <div className="dispute-text-container">
                <span className="dispute-text">This task has been marked as 'Bad Task'</span>
                {disputedTasks.get(currentTaskId)?.reason && (
                  <span className="dispute-reason">Reason: {disputedTasks.get(currentTaskId)?.reason}</span>
                )}
              </div>
              <button 
                className="dispute-remove-button"
                onClick={handleRemoveDispute}
                disabled={disputeLoading}
                title="Remove dispute"
              >
                ✕
              </button>
            </div>
          )}
          
          {/* Mark as Bad Task button - disabled if disputed or solution submitted */}
          <div className="bad-task-section">
            <button
              onClick={handleMarkAsBadTask}
              disabled={disputeLoading || (currentTaskId ? disputedTasks.has(currentTaskId) : false) || currentTask.is_completed}
              className={`bad-task-button ${currentTaskId && disputedTasks.has(currentTaskId) ? 'disputed' : ''} ${currentTask.is_completed ? 'submitted' : ''}`}
            >
              {disputeLoading ? (
                <>
                  <span className="button-spinner"></span>
                  Marking as Bad Task...
                </>
              ) : currentTaskId && disputedTasks.has(currentTaskId) ? (
                <>
                  ⚠️ Marked as Bad Task
                </>
              ) : (
                "Mark as 'Bad Task'"
              )}
            </button>
            {disputeError && (
              <div className="dispute-error">{disputeError}</div>
            )}
          </div>
          
          {currentTask.is_completed ? (
            <div className="completed-info">
              {/* Display all submitted solutions */}
              {currentTask.solutions && currentTask.solutions.length > 0 ? (
                currentTask.solutions.map((solution, index) => {
                  // Extract filename from file_paths if available
                  let fileName = null;
                  if (solution.solution_data?.file_paths && solution.solution_data.file_paths.length > 0) {
                    // Get the first file path and extract the filename (last part after /)
                    const filePath = solution.solution_data.file_paths[0];
                    fileName = filePath.split('/').pop();
                  }
                  
                  return (
                    <div key={solution.id} className="solution-card-container" style={{ marginBottom: '1rem' }}>
                      <div className="completed-file-card">
                        <span className="completed-file-icon">📄</span>
                        <div className="completed-file-info">
                          <div className="completed-file-name">
                            Solution #{index + 1}: {fileName || `${currentTask.problem.software.toLowerCase()} file`}
                          </div>
                          {solution.submission_notes && (
                            <div className="solution-notes">{solution.submission_notes}</div>
                          )}
                          <div className="solution-timestamp">
                            {new Date(solution.created_at).toLocaleString()}
                          </div>
                        </div>
                      <div className="completed-status-badge">
                        <span>✓</span>
                        <span>Submitted</span>
                      </div>
                      <button
                        onClick={() => {
                          setSolutionToDelete(solution.id);
                          setShowDeleteConfirm(true);
                        }}
                        className="solution-delete-button"
                        disabled={deletingLoading}
                        title="Delete this solution"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  );
                })
              ) : (
                // Fallback to single solution display for backward compatibility
                <div className="completed-file-card">
                  <span className="completed-file-icon">📄</span>
                  <div className="completed-file-info">
                    <div className="completed-file-name">
                      Solution Submitted ({currentTask.problem.software.toLowerCase()})
                    </div>
                  </div>
                  <div className="completed-status-badge">
                    <span>✓</span>
                    <span>Submitted</span>
                  </div>
                </div>
              )}
              
              {/* Delete Confirmation Dialog - Rendered using Portal */}
              {showDeleteConfirm && ReactDOM.createPortal(
                    <div 
                      className="delete-confirm-overlay"
                      onClick={(e) => {
                        // Close modal if clicking on overlay (not on dialog)
                        if (e.target === e.currentTarget) {
                          setShowDeleteConfirm(false);
                        }
                      }}
                    >
                      <div className="delete-confirm-dialog">
                        <h3>Confirm Deletion</h3>
                        <p>Are you sure you want to delete this solution? This action cannot be undone.</p>
                        <div className="delete-confirm-buttons">
                          <button
                            onClick={() => setShowDeleteConfirm(false)}
                            className="cancel-button"
                            disabled={deletingLoading}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleDeleteSolution}
                            className="confirm-delete-button"
                            disabled={deletingLoading}
                          >
                            {deletingLoading ? (
                              <>
                                <span className="button-spinner"></span>
                                Deleting...
                              </>
                            ) : (
                              'Delete Solution'
                            )}
                          </button>
                        </div>
                      </div>
                    </div>,
                    document.body
                  )}
              
              {/* Always show an additional upload area below submitted solution */}
              <div className="additional-solution-section">
                <div className="additional-solution-header">
                  <p>Upload another solution:</p>
                </div>
                <div className="desktop-submission">
                  <div className="submission-content">
                    <div 
                      className={`drop-zone ${isDragging ? 'dragging' : ''} ${currentSolutionFile ? 'has-file' : ''} ${currentTaskId && disputedTasks.has(currentTaskId) ? 'disputed' : ''}`}
                      onDrop={currentTaskId && disputedTasks.has(currentTaskId) ? undefined : handleFileDrop}
                      onDragOver={currentTaskId && disputedTasks.has(currentTaskId) ? undefined : handleDragOver}
                      onDragEnter={currentTaskId && disputedTasks.has(currentTaskId) ? undefined : handleDragEnter}
                      onDragLeave={currentTaskId && disputedTasks.has(currentTaskId) ? undefined : handleDragLeave}
                      onClick={() => !disputedTasks.has(currentTaskId || '') && document.getElementById('solution-file-input-additional')?.click()}
                    >
                      <input
                        id="solution-file-input-additional"
                        type="file"
                        accept={getAcceptExtensions(getValidExtensions(currentTask.problem.software.toLowerCase()))}
                        onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                        style={{ display: 'none' }}
                        disabled={currentTaskId ? disputedTasks.has(currentTaskId) : false}
                      />
                      
                      {currentSolutionFile ? (
                        <div className="file-selected">
                          <div className="file-info">
                            <span className="file-icon">📄</span>
                            <span className="file-name">
                              {currentSolutionFile.name}
                              <span className="file-size">({(currentSolutionFile.size / 1024 / 1024).toFixed(3)} MB)</span>
                            </span>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                if (currentTaskId) {
                                  setSolutionFiles(prev => {
                                    const updated = { ...prev };
                                    delete updated[currentTaskId];
                                    return updated;
                                  });
                                }
                                setFileError(null);
                              }}
                              className="remove-file"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="drop-zone-content">
                          <span className="drop-zone-icon">📁</span>
                          <p className="drop-zone-text">
                            <strong>Drop your solution file here</strong>
                          </p>
                          <p className="drop-zone-text">or click to browse</p>
                          <p className="drop-zone-hint">
                            Accepted formats: {getValidExtensions(currentTask.problem.software.toLowerCase()).join(', ')}
                          </p>
                        </div>
                      )}
                    </div>

                    {currentSolutionFile && (
                      <button
                        onClick={handleSubmitSolution}
                        disabled={uploading || (currentTaskId ? disputedTasks.has(currentTaskId) : false)}
                        className={`submit-solution-button ${currentTaskId && disputedTasks.has(currentTaskId) ? 'disputed' : ''}`}
                      >
                        {uploading ? (
                          <>
                            <span className="button-spinner"></span>
                            Submitting...
                          </>
                        ) : (
                          'Submit Solution'
                        )}
                      </button>
                    )}
                  </div>
                  
                  {fileError && (
                    <div className="file-error">{fileError}</div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="upload-section">
              {/* Only show impossible task option if the problem spec indicates it's impossible */}
              {currentTask.problem.spec.possible === false && (
                <div className="impossible-task-section">
                  <button
                    onClick={() => setShowImpossibleTaskModal(true)}
                    className="impossible-task-button"
                    disabled={uploading}
                  >
                    Explain Impossible Task
                  </button>
                  <p className="impossible-task-note">
                    This task has been marked as impossible to complete.
                  </p>
                </div>
              )}
              
              {isWebApp(currentTask.problem.software) ? (
                <div className="web-submission">
                  <p>This is a web application task. Click the button below to start recording your solution in the playground:</p>
                  <button
                    onClick={handleOpenPlayground}
                    className="playground-button"
                    disabled={creatingPlayground}
                  >
                    {creatingPlayground ? 'Creating Playground...' : 'Record Solution'}
                  </button>
                  <p className="playground-note">
                    The playground will open with the problem's initial state. You can then demonstrate your solution which will be recorded.
                  </p>
                </div>
              ) : currentTask.problem.spec.possible !== false ? (
                <div className="desktop-submission">
                  <div className="submission-header">
                    <p>Upload your solution file for this task:</p>
                  </div>
                  
                  <div className="submission-content">
                    <div 
                      className={`drop-zone ${isDragging ? 'dragging' : ''} ${currentSolutionFile ? 'has-file' : ''} ${currentTaskId && submittedTasks.has(currentTaskId) ? 'submitted' : ''} ${currentTaskId && disputedTasks.has(currentTaskId) ? 'disputed' : ''}`}
                      onDrop={currentTaskId && disputedTasks.has(currentTaskId) ? undefined : handleFileDrop}
                      onDragOver={currentTaskId && disputedTasks.has(currentTaskId) ? undefined : handleDragOver}
                      onDragEnter={currentTaskId && disputedTasks.has(currentTaskId) ? undefined : handleDragEnter}
                      onDragLeave={currentTaskId && disputedTasks.has(currentTaskId) ? undefined : handleDragLeave}
                      onClick={() => !submittedTasks.has(currentTaskId || '') && !disputedTasks.has(currentTaskId || '') && document.getElementById('solution-file-input')?.click()}
                    >
                      <input
                        id="solution-file-input"
                        type="file"
                        accept={getAcceptExtensions(getValidExtensions(currentTask.problem.software.toLowerCase()))}
                        onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                        style={{ display: 'none' }}
                        disabled={currentTaskId ? (submittedTasks.has(currentTaskId) || disputedTasks.has(currentTaskId)) : false}
                      />
                      
                      {currentSolutionFile ? (
                        <div className={`file-selected ${currentTaskId && submittedTasks.has(currentTaskId) ? 'submitted' : ''}`}>
                          <div className="file-info">
                            <span className="file-icon">📄</span>
                            <span className="file-name">
                              {currentSolutionFile.name}
                              <span className="file-size">({(currentSolutionFile.size / 1024 / 1024).toFixed(3)} MB)</span>
                            </span>
                            {currentTaskId && submittedTasks.has(currentTaskId) ? (
                              <div className="submitted-indicator">
                                <span>✓</span>
                                <span>Submitted</span>
                              </div>
                            ) : (
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (currentTaskId) {
                                    setSolutionFiles(prev => {
                                      const updated = { ...prev };
                                      delete updated[currentTaskId];
                                      return updated;
                                    });
                                  }
                                  setFileError(null);
                                }}
                                className="remove-file"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="drop-zone-content">
                          <span className="drop-zone-icon">📁</span>
                          <p className="drop-zone-text">
                            <strong>Drop your solution file here</strong>
                          </p>
                          <p className="drop-zone-text">or click to browse</p>
                          <p className="drop-zone-hint">
                            Accepted formats: {getValidExtensions(currentTask.problem.software.toLowerCase()).join(', ')}
                          </p>
                        </div>
                      )}
                    </div>

                    {currentSolutionFile && (
                      <button
                        onClick={handleSubmitSolution}
                        disabled={uploading || (currentTaskId ? (submittedTasks.has(currentTaskId) || disputedTasks.has(currentTaskId)) : false)}
                        className={`submit-solution-button ${currentTaskId && submittedTasks.has(currentTaskId) ? 'submitted' : ''} ${currentTaskId && disputedTasks.has(currentTaskId) ? 'disputed' : ''}`}
                      >
                        {uploading ? (
                          <>
                            <span className="button-spinner"></span>
                            Submitting...
                          </>
                        ) : currentTaskId && submittedTasks.has(currentTaskId) ? (
                          <>
                            ✓ Submitted
                          </>
                        ) : (
                          'Submit Solution'
                        )}
                      </button>
                    )}
                  </div>
                  
                  {fileError && (
                    <div className="file-error">{fileError}</div>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>
        
        {/* Navigation Controls at Bottom */}
        <div className="navigation-section">
          <div className="navigation-controls">
            <button
              onClick={handlePrevious}
              disabled={currentTaskIndex === 0}
              className="nav-button nav-button-previous"
            >
              <span className="nav-icon">←</span>
              <span className="nav-text">Previous</span>
            </button>
            
            {isLastTask() ? (
              <button
                onClick={handleFinishProject}
                disabled={!areAllSolutionsSubmitted()}
                className={`nav-button nav-button-finish ${areAllSolutionsSubmitted() ? 'enabled' : 'disabled'}`}
              >
                <span className="nav-text">Finish Project</span>
                <span className="nav-icon">🏁</span>
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!canNavigateNext()}
                className="nav-button nav-button-next"
              >
                <span className="nav-text">Next</span>
                <span className="nav-icon">→</span>
              </button>
            )}
          </div>
        </div>
        </>
        )}
      </div>
      </div>

      {/* Impossible Task Explanation Modal */}
      {showImpossibleTaskModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowImpossibleTaskModal(false)}>
          <div className="modal-content impossible-task-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Explain Impossible Task</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowImpossibleTaskModal(false)}
                disabled={uploading}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p>Please explain why this task cannot be completed as described:</p>
              <textarea
                className="impossible-task-reason-input"
                value={impossibleTaskReason}
                onChange={(e) => setImpossibleTaskReason(e.target.value)}
                placeholder="Explain why the task is impossible (e.g., missing information, contradictory requirements, technical limitations)..."
                rows={6}
                disabled={uploading}
                autoFocus
              />
              {fileError && (
                <div className="error-message">{fileError}</div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="modal-button modal-button-cancel"
                onClick={() => {
                  setShowImpossibleTaskModal(false);
                  setImpossibleTaskReason("");
                  setFileError(null);
                }}
                disabled={uploading}
              >
                Cancel
              </button>
              <button 
                className="modal-button modal-button-submit"
                onClick={handleSubmitImpossibleTask}
                disabled={uploading || !impossibleTaskReason.trim()}
              >
                {uploading ? 'Submitting...' : 'Submit Explanation'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Dispute Reason Modal */}
      {showDisputeModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowDisputeModal(false)}>
          <div className="modal-content dispute-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Mark as Bad Task</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowDisputeModal(false)}
                disabled={disputeLoading}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p>Please provide a reason for marking this task as bad:</p>
              <textarea
                className="dispute-reason-input"
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                placeholder="Enter your reason here..."
                rows={4}
                disabled={disputeLoading}
                autoFocus
              />
              {disputeError && (
                <div className="error-message">{disputeError}</div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="modal-button modal-button-cancel"
                onClick={() => setShowDisputeModal(false)}
                disabled={disputeLoading}
              >
                Cancel
              </button>
              <button 
                className="modal-button modal-button-submit"
                onClick={handleSubmitDispute}
                disabled={disputeLoading || !disputeReason.trim()}
              >
                {disputeLoading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Playground Recording Modal */}
      {showRecordingModal && currentTask && (
        <PlaygroundRecordingModal
          isOpen={showRecordingModal}
          onClose={() => {
            setShowRecordingModal(false);
            setPlaygroundPreview(null);
          }}
          playgroundPreview={playgroundPreview}
          onPlaygroundPreviewUpdate={(preview) => setPlaygroundPreview(preview)}
          software={currentTask.problem.software}
          problemId={currentTask.problem.id}
          problemName={currentTask.problem.name}
          taskDescription={currentTask.problem.spec.prompt || ""}
          projectId={projectId}
          onSolutionSubmitted={handlePlaygroundSolutionSubmitted}
          isImpossibleTask={currentTask.problem.spec.possible === false}
        />
      )}
    </div>
  );
};

export default SolutionTaskViewer;